import { useState, useRef, useEffect } from "react";
import { useNotifications } from "./NotificationContext";

const TYPE_ICON = {
  match_request:  "⚡",
  match_accepted: "🎉",
  match_rejected: "😔",
  chat_message:   "💬",
};

export function NotificationToast() {
  const { toast, setToast } = useNotifications();
  if (!toast) return null;
  return (
    <div style={s.toast} onClick={() => setToast(null)}>
      <span style={{ fontSize: 20, flexShrink: 0 }}>{TYPE_ICON[toast.type] || "🔔"}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{toast.title}</div>
        {toast.body && (
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {toast.body}
          </div>
        )}
      </div>
      <button style={s.toastClose} onClick={(e) => { e.stopPropagation(); setToast(null); }}>×</button>
    </div>
  );
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, settings, updateSettings } =
    useNotifications();

  const [open, setOpen]               = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const wrapRef = useRef(null);

  // 패널 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setShowSettings(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const toggleOpen = () => {
    setOpen((v) => !v);
    setShowSettings(false);
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60)    return "방금";
    if (diff < 3600)  return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    return `${Math.floor(diff / 86400)}일 전`;
  };

  return (
    <div ref={wrapRef} style={s.wrap}>
      {/* 벨 버튼 */}
      <button style={s.bellBtn} onClick={toggleOpen} aria-label="알림">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke={open ? "#E24B4A" : "#374151"} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={s.badge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>

      {/* 드롭다운 패널 */}
      {open && (
        <div style={s.panel}>
          {!showSettings ? (
            <>
              {/* 패널 헤더 */}
              <div style={s.panelHeader}>
                <span style={s.panelTitle}>🔔 알림</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {unreadCount > 0 && (
                    <button style={s.readAllBtn} onClick={markAllAsRead}>모두 읽음</button>
                  )}
                  {/* 설정 버튼 */}
                  <button style={s.iconBtn} onClick={() => setShowSettings(true)} title="알림 설정">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* 알림 목록 */}
              <div style={s.list}>
                {notifications.length === 0 ? (
                  <div style={s.empty}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>🔕</div>
                    <div style={{ fontSize: 13, color: "#9ca3af" }}>아직 알림이 없어요</div>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n.id}
                      style={{ ...s.notifItem, background: n.is_read ? "#fff" : "#fff8f8" }}
                      onClick={() => {
                        if (!n.is_read) markAsRead(n.id);
                        setOpen(false);
                        setShowSettings(false);
                        window.dispatchEvent(new CustomEvent("notif-navigate", {
                          detail: { type: n.type, relatedId: n.related_id },
                        }));
                      }}
                    >
                      <div style={s.notifIcon}>{TYPE_ICON[n.type] || "🔔"}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, color: "#111827",
                          fontWeight: n.is_read ? 500 : 700,
                        }}>{n.title}</div>
                        {n.body && (
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                            {n.body}
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: "#c4c9d4", marginTop: 4 }}>
                          {timeAgo(n.created_at)}
                        </div>
                      </div>
                      {!n.is_read && <div style={s.unreadDot} />}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            /* 설정 화면 */
            <>
              <div style={s.panelHeader}>
                <button style={s.iconBtn} onClick={() => setShowSettings(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="#374151" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <span style={s.panelTitle}>알림 설정</span>
                <div style={{ width: 28 }} />
              </div>

              <div style={s.settingsList}>
                <SettingItem label="알림 소리" sub="새 알림 시 소리 재생"
                  checked={settings.sound} onChange={(v) => updateSettings("sound", v)} />
                <SettingItem label="팝업 알림" sub="화면에 배너 표시"
                  checked={settings.popup} onChange={(v) => updateSettings("popup", v)} />

                <div style={s.settingSection}>알림 유형</div>

                <SettingItem label="⚡ 매칭 신청" sub="새 매칭 신청이 왔을 때"
                  checked={settings.match_request} onChange={(v) => updateSettings("match_request", v)} />
                <SettingItem label="🎉 수락 / 😔 거절" sub="신청한 매칭 결과를 받았을 때"
                  checked={settings.match_accepted} onChange={(v) => updateSettings("match_accepted", v)} />
                <SettingItem label="💬 채팅 메시지" sub="채팅방에 새 메시지가 왔을 때"
                  checked={settings.chat_message} onChange={(v) => updateSettings("chat_message", v)} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function NotificationSettingsModal({ onClose }) {
  const { settings, updateSettings } = useNotifications();
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "0 16px", zIndex: 500,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: 16,
          width: "100%", maxWidth: 340,
          padding: "24px 20px",
          maxHeight: "80vh", overflowY: "auto",
          fontFamily: "'Noto Sans KR', sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>🔔 알림 설정</span>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#9ca3af", lineHeight: 1, padding: "0 2px" }}
          >✕</button>
        </div>
        {/* 설정 항목 */}
        <SettingItem label="알림 소리" sub="새 알림 시 소리 재생"
          checked={settings.sound} onChange={(v) => updateSettings("sound", v)} />
        <SettingItem label="팝업 알림" sub="화면에 배너 표시"
          checked={settings.popup} onChange={(v) => updateSettings("popup", v)} />
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", padding: "12px 0 6px", letterSpacing: 0.5, borderTop: "1px solid #f0f1f5" }}>
          알림 유형
        </div>
        <SettingItem label="⚡ 매칭 신청" sub="새 매칭 신청이 왔을 때"
          checked={settings.match_request} onChange={(v) => updateSettings("match_request", v)} />
        <SettingItem label="🎉 수락 / 😔 거절" sub="신청한 매칭 결과를 받았을 때"
          checked={settings.match_accepted} onChange={(v) => updateSettings("match_accepted", v)} />
        <SettingItem label="💬 채팅 메시지" sub="채팅방에 새 메시지가 왔을 때"
          checked={settings.chat_message} onChange={(v) => updateSettings("chat_message", v)} />
      </div>
    </div>
  );
}

function SettingItem({ label, sub, checked, onChange }) {
  return (
    <div style={s.settingItem}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
      </div>
      <button
        style={{ ...s.toggle, background: checked ? "#E24B4A" : "#d1d5db" }}
        onClick={() => onChange(!checked)}
      >
        <div style={{ ...s.knob, transform: checked ? "translateX(20px)" : "translateX(2px)" }} />
      </button>
    </div>
  );
}

const s = {
  wrap: { position: "relative" },
  bellBtn: {
    position: "relative", background: "none", border: "none",
    cursor: "pointer", padding: 4,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  badge: {
    position: "absolute", top: -1, right: -1,
    background: "#E24B4A", color: "#fff",
    fontSize: 9, fontWeight: 700, borderRadius: 999,
    minWidth: 16, height: 16, padding: "0 3px",
    display: "flex", alignItems: "center", justifyContent: "center",
    border: "2px solid #fff",
  },
  panel: {
    position: "absolute", top: "calc(100% + 10px)", right: 0,
    width: 310, maxHeight: 460,
    background: "#fff", borderRadius: 16,
    boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
    border: "1px solid #e5e7eb",
    display: "flex", flexDirection: "column",
    overflow: "hidden", zIndex: 300,
  },
  panelHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 16px 12px",
    borderBottom: "1px solid #f0f1f5",
    flexShrink: 0,
  },
  panelTitle: { fontSize: 15, fontWeight: 700, color: "#111827" },
  readAllBtn: {
    fontSize: 11, color: "#E24B4A", background: "none",
    border: "none", cursor: "pointer", fontWeight: 600, padding: "2px 6px",
  },
  iconBtn: {
    background: "none", border: "none", cursor: "pointer",
    padding: 4, display: "flex", alignItems: "center", justifyContent: "center",
  },
  list: { overflowY: "auto", flex: 1 },
  empty: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "40px 20px",
  },
  notifItem: {
    display: "flex", alignItems: "flex-start", gap: 10,
    padding: "12px 14px", cursor: "pointer",
    borderBottom: "1px solid #f5f6fa",
  },
  notifIcon: {
    width: 34, height: 34, background: "#f5f6fa", borderRadius: 9,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 15, flexShrink: 0, border: "1px solid #e5e7eb",
  },
  unreadDot: {
    width: 8, height: 8, background: "#E24B4A",
    borderRadius: "50%", flexShrink: 0, marginTop: 4,
  },
  settingsList: { overflowY: "auto", flex: 1 },
  settingSection: {
    fontSize: 11, fontWeight: 700, color: "#9ca3af",
    padding: "12px 16px 6px", letterSpacing: 0.5,
    borderTop: "1px solid #f0f1f5",
  },
  settingItem: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 16px", borderBottom: "1px solid #f5f6fa",
  },
  toggle: {
    width: 44, height: 24, borderRadius: 999,
    border: "none", cursor: "pointer", position: "relative",
    transition: "background 0.2s", flexShrink: 0,
  },
  knob: {
    position: "absolute", top: 2,
    width: 20, height: 20, background: "#fff",
    borderRadius: "50%", transition: "transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
  },
  // 토스트 (App 레벨에서 렌더링됨)
  toast: {
    position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)",
    width: "calc(100% - 32px)", maxWidth: 398,
    background: "#fff", borderRadius: 14,
    boxShadow: "0 4px 20px rgba(0,0,0,0.14)", border: "1px solid #e5e7eb",
    padding: "12px 14px",
    display: "flex", alignItems: "center", gap: 10,
    zIndex: 500, cursor: "pointer",
  },
  toastClose: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 20, color: "#9ca3af", padding: "0 2px", lineHeight: 1,
    flexShrink: 0,
  },
};
